"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { MdClose } from "react-icons/md";
import Input from "../ui/Input";
import Button from "../ui/Button";
import { authApi } from "@/lib/api";
import type { ApiError } from "@/types/auth";

type ForgotPasswordModalProps = {
  isOpen: boolean;
  onClose: () => void;
  email?: string;
};

type RequestFormValues = {
  email: string;
};

type ConfirmFormValues = {
  code: string;
  password: string;
  confirmPassword: string;
};

export default function ForgotPasswordModal({
  isOpen,
  onClose,
  email: initialEmail = "",
}: ForgotPasswordModalProps) {
  const [step, setStep] = useState<"request" | "confirm">("request");
  const [email, setEmail] = useState(initialEmail);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const requestForm = useForm<RequestFormValues>({
    defaultValues: { email: initialEmail },
  });

  const confirmForm = useForm<ConfirmFormValues>({
    defaultValues: { code: "", password: "", confirmPassword: "" },
  });

  const handleRequestSubmit = async (data: RequestFormValues) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await authApi.requestPasswordReset({ email: data.email });

      if (!response.emailExists) {
        setError(response.message || "No account found with this email address.");
        return;
      }

      setEmail(data.email);
      setStep("confirm");
      setSuccess(response.message || "Verification code sent to your email");
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || "Failed to send verification code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await authApi.requestPasswordReset({ email });

      if (!response.emailExists) {
        setError(response.message || "No account found with this email address.");
        return;
      }

      // Clear the code input
      confirmForm.setValue("code", "");
      setSuccess("Verification code resent to your email");
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || "Failed to resend verification code");
    } finally {
      setIsResending(false);
    }
  };

  const handleConfirmSubmit = async (data: ConfirmFormValues) => {
    if (data.password !== data.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await authApi.confirmPasswordReset({
        email,
        token: data.code,
        password: data.password,
      });
      setSuccess("Password reset successfully! You can now sign in.");
      setTimeout(() => {
        onClose();
        // Reset form state
        setStep("request");
        setEmail("");
        requestForm.reset();
        confirmForm.reset();
        setError(null);
        setSuccess(null);
      }, 2000);
    } catch (err: unknown) {
      console.error("Password reset error:", err);
      let errorMessage = "Failed to reset password";

      if (err && typeof err === 'object' && 'message' in err) {
        errorMessage = (err as ApiError).message || errorMessage;
      } else if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        errorMessage = axiosError.response?.data?.message || errorMessage;
      }

      console.log("Setting error message:", errorMessage);
      setError(errorMessage);
      setIsLoading(false); // Make sure loading is set to false
    }
  };

  const handleClose = () => {
    onClose();
    // Reset form state
    setStep("request");
    setEmail("");
    requestForm.reset();
    confirmForm.reset();
    setError(null);
    setSuccess(null);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      onClick={() => {
        // Don't close modal if there's an error or if loading
        if (!error && !isLoading && !isResending) {
          handleClose();
        }
      }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-xl mx-4"
      >
        <Button
          onClick={handleClose}
          variant="ghost"
          size="sm"
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-2"
          aria-label="Close modal"
        >
          <MdClose className="h-6 w-6" />
        </Button>

        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          {step === "request" ? "Forgot Password?" : "Reset Password"}
        </h2>

        {step === "request" ? (
          <form
            onSubmit={requestForm.handleSubmit(handleRequestSubmit)}
            className="space-y-4"
          >
            <p className="text-sm text-gray-600 mb-4">
              Enter your email address and we&apos;ll send you a verification code to
              reset your password.
            </p>

            <Input
              type="email"
              placeholder="Enter your email"
              {...requestForm.register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              })}
              error={requestForm.formState.errors.email?.message}
            />

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            {success && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 text-sm">{success}</p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                size="md"
                onClick={handleClose}
                className="px-6"
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="md"
                className="px-6"
                disabled={isLoading}
              >
                {isLoading ? "Sending..." : "Send Code"}
              </Button>
            </div>
          </form>
        ) : (
          <form
            onSubmit={confirmForm.handleSubmit(handleConfirmSubmit)}
            className="space-y-4"
          >
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-3">
                Enter the verification code sent to <strong>{email}</strong> and
                your new password.
              </p>
            </div>

            <Input
              type="text"
              placeholder="Enter verification code"
              {...confirmForm.register("code", {
                required: "Verification code is required",
                minLength: {
                  value: 4,
                  message: "Verification code must be at least 4 characters",
                },
              })}
              error={confirmForm.formState.errors.code?.message}
            />

            <Input
              type="password"
              placeholder="New password"
              showPasswordToggle={true}
              {...confirmForm.register("password", {
                required: "Password is required",
                minLength: {
                  value: 6,
                  message: "Password must be at least 6 characters",
                },
              })}
              error={confirmForm.formState.errors.password?.message}
            />

            <Input
              type="password"
              placeholder="Confirm new password"
              showPasswordToggle={true}
              {...confirmForm.register("confirmPassword", {
                required: "Please confirm your password",
              })}
              error={confirmForm.formState.errors.confirmPassword?.message}
            />

            <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Code expires in 15 minutes</span>

              <button
                type="button"
                onClick={handleResendCode}
                disabled={isResending || isLoading}
                className="text-sm font-medium text-brand-blue hover:underline disabled:text-gray-400 disabled:cursor-not-allowed disabled:no-underline"
              >
                {isResending ? "Resending code..." : "Resend code"}
              </button>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm font-medium">{error}</p>
              </div>
            )}

            {success && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 text-sm font-medium">{success}</p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                size="md"
                onClick={() => {
                  setStep("request");
                  setError(null);
                  setSuccess(null);
                  confirmForm.reset();
                }}
                className="px-6"
                disabled={isLoading || isResending}
              >
                Back
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="md"
                className="px-6"
                disabled={isLoading || isResending}
              >
                {isLoading ? "Resetting..." : "Reset Password"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

